import { useState, useEffect, useRef } from 'react'
import type { BrandColorCandidate, ClientProfile, ClientBranding, PurposeOption, Settings } from '@shared/types'
import { formatDashboardTitle } from '@tn/shared'
import LogoUpload from '../../components/LogoUpload'
import BrandLookupButton from '../../components/BrandLookupButton'
import BrandPreviewHeader from '../../components/BrandPreviewHeader'
import { useApi } from '../../contexts/ApiContext'
import { extractLogoPalette } from '../../utils/branding'

// Defined outside component so React doesn't remount on every render
// htmlFor ties the label to its control so screen readers (and getByLabel) find it.
function Field({ label, children, hint, optional, htmlFor }: { label: string; children: React.ReactNode; hint?: string; optional?: boolean; htmlFor?: string }) {
  return (
    <div className="mb-5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
        {optional && <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}


const HEADCOUNT_RANGES = [
  { label: '< 5,000', min: 0, max: 5000 },
  { label: '5K – 25K', min: 5000, max: 25000 },
  { label: '25K – 100K', min: 25000, max: 100000 },
  { label: '100K+', min: 100000, max: 500000 },
]

function getSelectedRange(min?: number, max?: number) {
  if (min === undefined || max === undefined) return null
  return HEADCOUNT_RANGES.find((r) => r.min === min && r.max === max) || null
}

interface Props {
  profile: Partial<ClientProfile>
  branding: Partial<ClientBranding>
  mmsId: string
  purposeOption: string
  industries: string[]
  purposeOptions: PurposeOption[]
  onProfileChange: (p: Partial<ClientProfile>) => void
  onMmsIdChange: (v: string) => void
  onPurposeOptionChange: (v: string) => void
  onColorChange: (color: string) => void
  onAccentChange: (color: string | undefined) => void
  onLogoChange: (logoDataUrl: string | undefined) => void
  onVersionChange: (versionLabel: string) => void
  onBack: () => void
  onNext: () => void
}

export default function Step1_ClientProfile({ profile, branding, mmsId, purposeOption, industries, purposeOptions, onProfileChange, onMmsIdChange, onPurposeOptionChange, onColorChange, onAccentChange, onLogoChange, onVersionChange, onBack, onNext }: Props) {
  const api = useApi()
  const mmsIdRequired = purposeOption !== '' && (purposeOptions.find((o) => o.value === purposeOption)?.requiresMmsId ?? true)
  const mmsIdError = mmsId.trim() !== '' && !/^\d{8}$/.test(mmsId.trim())
  const mmsIdMissing = mmsIdRequired && mmsId.trim() === ''

  // Local state so the user can type freely without the field resetting on each keystroke
  const [hexInput, setHexInput] = useState(branding.primaryColor || '#A100FF')
  const [accentInput, setAccentInput] = useState(branding.accentColor || '')
  const [headcountInput, setHeadcountInput] = useState(profile.targetHeadcount !== undefined ? String(profile.targetHeadcount) : '')

  // ── Logo color: an opt-in alternative, never an auto-apply (plan §4.3) ──
  const [logoColorCandidate, setLogoColorCandidate] = useState<string | null>(null)
  const [colorSource, setColorSource] = useState<'brand' | 'logo'>('brand')
  const [savedBrandColor, setSavedBrandColor] = useState<string | null>(null)
  const logoRequestRef = useRef(0)

  useEffect(() => {
    const requestId = ++logoRequestRef.current
    if (!branding.logoDataUrl) {
      setLogoColorCandidate(null)
      // Removing the logo also removes the "Use logo color" option — if that was the
      // active selection, revert the field to the saved brand color rather than leaving
      // it stranded on a now-orphaned logo-derived value with no toggle left to undo it.
      if (colorSource === 'logo') {
        const restored = savedBrandColor || branding.primaryColor || '#A100FF'
        setHexInput(restored)
        onColorChange(restored)
      }
      setColorSource('brand')
      return
    }
    // A freshly uploaded logo always supersedes whatever candidate the previous one
    // produced — re-extracting (rather than just clearing) is what makes replacing a
    // logo surface "Use logo color" again with the new logo's color.
    extractLogoPalette(branding.logoDataUrl).then((candidate) => {
      if (requestId === logoRequestRef.current) setLogoColorCandidate(candidate)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on a logo change, not on every colorSource/savedBrandColor update
  }, [branding.logoDataUrl])

  const useLogoColor = () => {
    if (!logoColorCandidate) return
    setSavedBrandColor(branding.primaryColor || null)
    setHexInput(logoColorCandidate)
    onColorChange(logoColorCandidate)
    setColorSource('logo')
  }

  const useBrandColor = () => {
    const restored = savedBrandColor || branding.primaryColor || '#A100FF'
    setHexInput(restored)
    onColorChange(restored)
    setColorSource('brand')
  }

  const handleColorChange = (color: string) => {
    setColorSource('brand')
    onColorChange(color)
  }

  // ── LLM-assisted brand color lookup (plans/v3-shell-brand-color-palette-plan.md §4.1) ──
  const [settings, setSettings] = useState<Settings | null>(null)
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle')
  const [lookupMessage, setLookupMessage] = useState<string | null>(null)
  // Candidates after the top (auto-applied) one — offered as clickable alternates.
  const [otherCandidates, setOtherCandidates] = useState<BrandColorCandidate[]>([])

  useEffect(() => {
    let mounted = true
    api.getSettings().then((s) => { if (mounted) setSettings(s) })
    return () => { mounted = false }
  }, [api])

  function applyCandidate(candidate: BrandColorCandidate) {
    setColorSource('brand')
    if (candidate.primaryColor) { onColorChange(candidate.primaryColor); setHexInput(candidate.primaryColor) }
    // Found a primary but no distinct accent — leave accent unset rather than inventing
    // one, per §4.2: the existing primaryColorDark/Light auto-derive fallback takes over.
    onAccentChange(candidate.accentColor ?? undefined)
    setAccentInput(candidate.accentColor ?? '')
  }

  async function handleLookupBrandColor() {
    const companyName = profile.companyName?.trim()
    if (!companyName || !settings || settings.aiProvider !== 'claude') return
    setLookupStatus('loading')
    setLookupMessage(null)
    setOtherCandidates([])
    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey
      : settings.aiProvider === 'azure-openai' ? settings.azureApiKey
      : settings.openaiApiKey
    try {
      const result = await api.lookupBrandColors(companyName, settings.aiProvider ?? 'claude', apiKey ?? '')
      if (result.verified && result.candidates.length > 0) {
        const [top, ...rest] = result.candidates
        applyCandidate(top)
        setOtherCandidates(rest)
        setLookupStatus('found')
      } else {
        setLookupStatus('not-found')
        setLookupMessage(`No public data about color or theme of ${companyName}. Upload a logo or use the color palette below.`)
      }
    } catch (err) {
      setLookupStatus('error')
      setLookupMessage(err instanceof Error ? err.message : 'Could not check brand color right now — please try again.')
    }
  }

  // Lookup is Claude-only (see brand-lookup.ts) — once settings are known, gate the button
  // instead of letting a non-Claude click silently resolve to the generic "not found" notice.
  const lookupUnsupportedProvider = !!settings && settings.aiProvider !== 'claude'

  // Keep in sync when the color picker (or external changes) update branding.primaryColor
  useEffect(() => {
    setHexInput(branding.primaryColor || '#A100FF')
  }, [branding.primaryColor])

  useEffect(() => {
    setAccentInput(branding.accentColor || '')
  }, [branding.accentColor])

  // Strip commas and spaces so analysts can type "5,535" or "5 535"
  const normalizedHeadcount = headcountInput.replace(/[\s,]/g, '')
  const headcountInputError = normalizedHeadcount !== '' && !/^\d+$/.test(normalizedHeadcount)
  const headcountInputZero = normalizedHeadcount !== '' && /^\d+$/.test(normalizedHeadcount) && parseInt(normalizedHeadcount, 10) === 0
  const headcountInputTooLarge = normalizedHeadcount !== '' && /^\d+$/.test(normalizedHeadcount) && parseInt(normalizedHeadcount, 10) > 5_000_000

  const isValid = !!profile.companyName?.trim() && !!purposeOption && !mmsIdError && !mmsIdMissing && !headcountInputError && !headcountInputZero && !headcountInputTooLarge

  const selectedRange = getSelectedRange(profile.headcountMin, profile.headcountMax)

  const selectRange = (range: typeof HEADCOUNT_RANGES[number] | null) => {
    if (!range) {
      onProfileChange({ ...profile, headcountMin: undefined, headcountMax: undefined })
    } else {
      // Selecting a range clears any exact headcount so the two can't conflict
      setHeadcountInput('')
      onProfileChange({ ...profile, headcountMin: range.min, headcountMax: range.max, targetHeadcount: undefined })
    }
  }

  return (
    <div className="max-w-xl">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Client Profile</h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter the client name and brand color — that's all you need. Claude will research the rest, or you can fill in optional details to guide the analysis.
      </p>

      {/* Required: Company Name */}
      <Field label="Company Name" htmlFor="oi-company-name">
        <input
          id="oi-company-name"
          type="text"
          value={profile.companyName || ''}
          onChange={(e) => onProfileChange({ ...profile, companyName: e.target.value })}
          placeholder="e.g. CVS Health, Toyota, HSBC…"
          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
        />
      </Field>

      {/* MMS ID and Purpose */}
      <div className="grid grid-cols-2 gap-x-6">
        <div className="mb-5">
          <label htmlFor="oi-mms-id" className="block text-sm font-semibold text-gray-700 mb-1.5">
            MMS ID
            {mmsIdRequired
              ? <span className="ml-1.5 text-xs font-normal text-red-500">(required)</span>
              : <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
            }
          </label>
          <input
            id="oi-mms-id"
            type="text"
            value={mmsId}
            onChange={(e) => onMmsIdChange(e.target.value)}
            placeholder="e.g. 12345678"
            className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${mmsIdError || mmsIdMissing ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-purple-300'}`}
          />
          {mmsIdError && <p className="text-xs text-red-500 mt-1">MMS ID must be exactly 8 digits.</p>}
          {mmsIdMissing && !mmsIdError && <p className="text-xs text-red-500 mt-1">MMS ID is required for this purpose.</p>}
        </div>
        <Field label="Purpose" htmlFor="oi-purpose">
          <select
            id="oi-purpose"
            value={purposeOption}
            onChange={(e) => {
              const selected = purposeOptions.find((o) => o.value === e.target.value)
              onPurposeOptionChange(e.target.value)
              if (selected?.defaultMmsId) {
                onMmsIdChange(selected.defaultMmsId)
              } else if (purposeOptions.find((o) => o.value === purposeOption)?.defaultMmsId) {
                onMmsIdChange('')
              }
            }}
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          >
            <option value="" disabled hidden>Select purpose…</option>
            {purposeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* Optional: Client Logo — comes first so its extracted color can inform Brand Color below */}
      <LogoUpload value={branding.logoDataUrl} onChange={onLogoChange} />

      {/* Required: Brand Color */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="oi-brand-color" className="block text-sm font-semibold text-gray-700">Client Brand Color</label>
          <BrandLookupButton
            onClick={() => void handleLookupBrandColor()}
            disabled={!profile.companyName?.trim() || lookupStatus === 'loading' || lookupUnsupportedProvider}
            loading={lookupStatus === 'loading'}
            title={lookupUnsupportedProvider ? 'Brand color lookup requires the Claude provider — change it in Settings.' : undefined}
          />
        </div>

        {/* Mirrors the real v3 dashboard header (solid color, not a gradient) so what's
            previewed here matches what actually ships. */}
        <BrandPreviewHeader
          primaryColor={branding.primaryColor || '#A100FF'}
          accentColor={branding.accentColor}
          logoUrl={branding.logoDataUrl}
          title={formatDashboardTitle(profile.companyName || 'Client')}
          versionPillText={branding.versionLabel?.trim() || undefined}
          showDraftPill
        />

        <div className="flex items-center gap-4 mt-3">
          <input
            type="color"
            aria-label="Client brand color picker"
            value={branding.primaryColor || '#A100FF'}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <input
            id="oi-brand-color"
            type="text"
            value={hexInput}
            onChange={(e) => {
              const val = e.target.value
              setHexInput(val)
              if (/^#[0-9A-Fa-f]{6}$/.test(val)) handleColorChange(val)
            }}
            placeholder="#A100FF"
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg font-mono w-32 focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">This color will brand the entire navigator — header, tabs, metric cards, and accents.</p>

        {lookupStatus === 'not-found' && lookupMessage && (
          <p role="status" className="text-xs text-gray-500 mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            {lookupMessage}
          </p>
        )}
        {lookupStatus === 'error' && lookupMessage && (
          <p role="alert" className="text-xs text-red-500 mt-2">
            {lookupMessage}
          </p>
        )}
        {lookupStatus === 'found' && (
          <p className="text-xs text-purple-600 mt-2">
            Updated from publicly available brand data — review and adjust if needed.
          </p>
        )}

        {/* Other ranked candidates (plan §4.1) — the top one is auto-applied above; these are
            one click away, so a weaker/alternate search match isn't silently discarded. */}
        {lookupStatus === 'found' && otherCandidates.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1.5">Other suggested colors for {profile.companyName}:</p>
            <div className="flex items-center gap-2">
              {otherCandidates.map((candidate, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyCandidate(candidate)}
                  title={candidate.primaryColor ?? undefined}
                  aria-label={`Use suggested color ${candidate.primaryColor ?? ''}`}
                  className="w-7 h-7 rounded-md border-2 border-gray-200 hover:border-purple-400 transition-all flex-shrink-0"
                  style={{ background: candidate.primaryColor ?? '#D1D5DB' }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Color source: each option only appears once its source has actually produced a
            color this session — "Use brand color" needs a successful lookup, "Use logo
            color" needs an extractable logo candidate. Neither, one, or both can be true. */}
        {(lookupStatus === 'found' || logoColorCandidate) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-gray-500">Color source:</span>
            {lookupStatus === 'found' && (
              <button
                type="button"
                onClick={useBrandColor}
                className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all ${
                  colorSource === 'brand' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                Use brand color
              </button>
            )}
            {logoColorCandidate && (
              <button
                type="button"
                onClick={useLogoColor}
                className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all flex items-center gap-1.5 ${
                  colorSource === 'logo' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                <span className="inline-block w-3 h-3 rounded-full border border-gray-200" style={{ background: logoColorCandidate }} />
                Use logo color
              </button>
            )}
          </div>
        )}
      </div>

      {/* Optional: Accent Color */}
      <div className="mb-6">
        <label htmlFor="oi-accent-color" className="block text-sm font-semibold text-gray-700 mb-1.5">
          Accent / Secondary Color
          <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-4">
          <input
            type="color"
            aria-label="Accent color picker"
            value={branding.accentColor || '#A100FF'}
            onChange={(e) => onAccentChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <input
            id="oi-accent-color"
            type="text"
            value={accentInput}
            onChange={(e) => {
              const val = e.target.value
              setAccentInput(val)
              if (val === '') onAccentChange(undefined)
              else if (/^#[0-9A-Fa-f]{6}$/.test(val)) onAccentChange(val)
            }}
            placeholder="e.g. #0047BB (leave blank to auto-derive)"
            className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
          {branding.accentColor && (
            <button
              type="button"
              onClick={() => { setAccentInput(''); onAccentChange(undefined) }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Dashboard Version — shown as a pill in the header; defaults to V1, freely editable */}
      <Field label="Dashboard Version" optional htmlFor="oi-version" hint="Shown as a pill in the dashboard header. Defaults to V1 and can be edited later.">
        <input
          id="oi-version"
          type="text"
          value={branding.versionLabel ?? ''}
          onChange={(e) => onVersionChange(e.target.value)}
          placeholder="V1"
          maxLength={60}
          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
        />
      </Field>

      {/* Optional section divider */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-xs text-gray-400 font-medium">Optional — Claude will research these if left blank</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>

      {/* Optional: Industry + Geography */}
      <div className="grid grid-cols-2 gap-x-6">
        <Field label="Industry" optional htmlFor="oi-industry">
          <select
            id="oi-industry"
            value={profile.industry || ''}
            onChange={(e) => onProfileChange({ ...profile, industry: e.target.value || undefined })}
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          >
            <option value="">Talent Navigator will infer...</option>
            {industries.map((ind) => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </Field>

        <Field label="Geography / Market" optional htmlFor="oi-geography">
          <input
            id="oi-geography"
            type="text"
            value={profile.geography || ''}
            onChange={(e) => onProfileChange({ ...profile, geography: e.target.value || undefined })}
            placeholder="e.g. United States, UK, Global"
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          />
        </Field>
      </div>

      {/* Optional: Headcount range chips */}
      <Field label="Approximate Headcount" optional hint="Leave blank for Claude to research based on public data">
        <div className="flex gap-2 flex-wrap">
          {HEADCOUNT_RANGES.map((range) => {
            const isSelected = selectedRange?.label === range.label
            return (
              <button
                key={range.label}
                type="button"
                aria-pressed={isSelected}
                onClick={() => selectRange(isSelected ? null : range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {range.label}
              </button>
            )
          })}
        </div>
      </Field>

      {/* Optional: Exact known headcount — overrides AI estimate */}
      <Field
        label="In-Scope Employee Headcount"
        optional
        htmlFor="oi-target-headcount"
        hint="If you know the actual headcount, enter it here to override the AI estimate. FTEs for the functions being analyzed, not the company total."
      >
        <input
          id="oi-target-headcount"
          type="text"
          inputMode="numeric"
          value={headcountInput}
          onChange={(e) => {
            const raw = e.target.value
            setHeadcountInput(raw)
            const normalized = raw.replace(/[\s,]/g, '')
            if (normalized === '') {
              onProfileChange({ ...profile, targetHeadcount: undefined })
            } else if (/^\d+$/.test(normalized)) {
              const n = parseInt(normalized, 10)
              // Entering an exact headcount clears the range chips so the two can't conflict
              onProfileChange({ ...profile, targetHeadcount: n > 0 ? n : undefined, headcountMin: undefined, headcountMax: undefined })
            } else {
              // Invalid characters — clear the stored value so stale data isn't used
              onProfileChange({ ...profile, targetHeadcount: undefined })
            }
          }}
          placeholder="e.g. 5535"
          className={`w-1/2 px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${headcountInputError || headcountInputZero || headcountInputTooLarge ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-purple-300'}`}
        />
        {headcountInputError && <p className="text-xs text-red-500 mt-1">Please enter numbers only.</p>}
        {headcountInputZero && <p className="text-xs text-red-500 mt-1">Headcount must be greater than zero.</p>}
        {headcountInputTooLarge && <p className="text-xs text-red-500 mt-1">Headcount must be 5,000,000 or less.</p>}
      </Field>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-6 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="py-3 px-8 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
        >
          Next: Function Scope →
        </button>
      </div>
    </div>
  )
}
